import { HexVersionId } from "@hex/common";
import { PayloadAction, createSlice } from "@reduxjs/toolkit";

import { DataAppChromeColors } from "../../util/dataAppChromeTheme.js";
import { RootState } from "../store";

const MAX_RUNTIME_ERRORS = 20;

export interface DataAppRuntimeError {
  message: string;
  stack?: string;
  timestamp: number;
}

/**
 * Chrome colors currently being applied to host UI (AppNavbar) next to the
 * data app. Populated from the runtime-sampled value postMessaged by the
 * iframe after render.
 */
export type DataAppThemeState = DataAppChromeColors;

export interface DataAppVersionState {
  files: Record<string, string> | null;
  entryPoint: string | null;
  lastModifiedAt: number | null;
  isLoading: boolean;
  runtimeErrors: DataAppRuntimeError[];
  appTheme: DataAppThemeState | null;
}

type DataAppSliceState = Record<HexVersionId, DataAppVersionState | undefined>;

const initialVersionState = (): DataAppVersionState => ({
  files: null,
  entryPoint: null,
  lastModifiedAt: null,
  isLoading: false,
  runtimeErrors: [],
  appTheme: null,
});

const dataAppSlice = createSlice({
  name: "dataApp",
  initialState: {} as DataAppSliceState,
  reducers: {
    setDataAppLoading: (
      state,
      action: PayloadAction<{ hexVersionId: HexVersionId }>,
    ) => {
      const { hexVersionId } = action.payload;
      const existing = state[hexVersionId] ?? initialVersionState();
      state[hexVersionId] = { ...existing, isLoading: true };
    },
    setDataAppState: (
      state,
      action: PayloadAction<{
        hexVersionId: HexVersionId;
        files: Record<string, string>;
        entryPoint: string;
        lastModifiedAt: number;
      }>,
    ) => {
      const { entryPoint, files, hexVersionId, lastModifiedAt } =
        action.payload;
      const existing = state[hexVersionId] ?? initialVersionState();
      state[hexVersionId] = {
        ...existing,
        files,
        entryPoint,
        lastModifiedAt,
        isLoading: false,
        runtimeErrors: [],
      };
    },
    setDataAppTheme: (
      state,
      action: PayloadAction<{
        hexVersionId: HexVersionId;
        theme: DataAppThemeState | null;
      }>,
    ) => {
      const { hexVersionId, theme } = action.payload;
      const existing = state[hexVersionId] ?? initialVersionState();
      state[hexVersionId] = { ...existing, appTheme: theme };
    },
    setDataAppNotFound: (
      state,
      action: PayloadAction<{ hexVersionId: HexVersionId }>,
    ) => {
      const { hexVersionId } = action.payload;
      state[hexVersionId] = { ...initialVersionState(), isLoading: false };
    },
    addRuntimeError: (
      state,
      action: PayloadAction<{
        hexVersionId: HexVersionId;
        error: DataAppRuntimeError;
      }>,
    ) => {
      const { error, hexVersionId } = action.payload;
      const existing = state[hexVersionId] ?? initialVersionState();
      const errors = [...existing.runtimeErrors, error].slice(
        -MAX_RUNTIME_ERRORS,
      );
      state[hexVersionId] = { ...existing, runtimeErrors: errors };
    },
    clearRuntimeErrors: (
      state,
      action: PayloadAction<{ hexVersionId: HexVersionId }>,
    ) => {
      const { hexVersionId } = action.payload;
      const existing = state[hexVersionId];
      if (existing != null) {
        state[hexVersionId] = { ...existing, runtimeErrors: [] };
      }
    },
  },
});

export const {
  addRuntimeError,
  clearRuntimeErrors,
  setDataAppLoading,
  setDataAppNotFound,
  setDataAppState,
  setDataAppTheme,
} = dataAppSlice.actions;

export const dataAppReducer = dataAppSlice.reducer;

const selectVersionState = (
  state: RootState,
  hexVersionId: HexVersionId,
): DataAppVersionState | undefined => state.dataApp[hexVersionId];

export const dataAppSelectors = {
  selectFiles: (state: RootState, hexVersionId: HexVersionId) =>
    selectVersionState(state, hexVersionId)?.files ?? null,

  // True once we've heard back from the server (either `setDataAppState` or
  // `setDataAppNotFound`). Before that, `selectFiles` returns `null` both for
  // the pre-load case and the genuinely-empty case, and callers that need to
  // distinguish "not loaded yet" from "confirmed no files" should use this.
  selectIsLoaded: (state: RootState, hexVersionId: HexVersionId) => {
    const versionState = selectVersionState(state, hexVersionId);
    return versionState != null && !versionState.isLoading;
  },

  selectEntryPoint: (state: RootState, hexVersionId: HexVersionId) =>
    selectVersionState(state, hexVersionId)?.entryPoint ?? null,

  selectIsLoading: (state: RootState, hexVersionId: HexVersionId) =>
    selectVersionState(state, hexVersionId)?.isLoading ?? false,

  selectRuntimeErrors: (state: RootState, hexVersionId: HexVersionId) =>
    selectVersionState(state, hexVersionId)?.runtimeErrors ?? [],

  selectHasApp: (state: RootState, hexVersionId: HexVersionId) =>
    (selectVersionState(state, hexVersionId)?.files ?? null) != null,

  selectLastModifiedAt: (state: RootState, hexVersionId: HexVersionId) =>
    selectVersionState(state, hexVersionId)?.lastModifiedAt ?? null,

  selectAppTheme: (state: RootState, hexVersionId: HexVersionId) =>
    selectVersionState(state, hexVersionId)?.appTheme ?? null,
};
